import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    GraduationCap, Search, UserPlus, Wallet, BookOpen, CheckCircle2,
    TrendingUp, Sparkles, Mail, Trash2, Loader2, RotateCcw, AlertTriangle,
    Download, FileText,
} from "lucide-react";
import StudentEnrollmentWizard from "@/components/admin/StudentEnrollmentWizard";
import EditStudentDialog from "@/components/admin/EditStudentDialog";
import StudentRowActions from "@/components/admin/StudentRowActions";
import ExportCsvDialog from "@/components/admin/ExportCsvDialog";
import ExtensionRequestsTab from "@/components/admin/ExtensionRequestsTab";
import PresenceIndicator from "@/components/admin/PresenceIndicator";
import RequestPurgeDialog from "@/components/admin/RequestPurgeDialog";
import EmptyTrashButton from "@/components/admin/EmptyTrashButton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return iso;
    }
}

function PaymentBadge({ mode }) {
    const label = (mode || "online_stripe").replace(/_/g, " ");
    const cls =
        mode === "paid_offline"
            ? "border-primary/40 text-primary bg-primary/5"
            : mode === "waived"
              ? "border-accent/50 text-accent-foreground bg-accent/15"
              : "border-border text-muted-foreground bg-muted/40";
    return (
        <Badge variant="outline" className={`text-[10px] capitalize ${cls}`}>
            <Wallet className="h-2.5 w-2.5 mr-1" />
            {label}
        </Badge>
    );
}

function KpiTile({ label, value, suffix, Icon, testId }) {
    return (
        <Card
            className="surface-card border-border"
            data-testid={testId}
        >
            <CardContent className="p-5">
                <div className="flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                        {label}
                    </div>
                    {Icon && (
                        <div className="h-8 w-8 rounded-md bg-primary/8 text-primary flex items-center justify-center">
                            <Icon className="h-4 w-4" />
                        </div>
                    )}
                </div>
                <div className="font-display text-3xl text-foreground mt-2 leading-none">
                    {value}
                    {suffix && (
                        <span className="text-base text-muted-foreground ml-1">{suffix}</span>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

export default function AdminStudents() {
    const [students, setStudents] = useState([]);
    const [trashed, setTrashed] = useState([]);
    const [extensionRequests, setExtensionRequests] = useState([]);
    const [view, setView] = useState("active"); // "active" | "trash" | "requests"
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [wizardOpen, setWizardOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editingStudent, setEditingStudent] = useState(null);
    // "all" (default) | "loa_pending" — controls which subset of the
    // students table is visible. Pending-LOA filter checks every student's
    // enrollments for `loa_status === "pending"`.
    const [activeFilter, setActiveFilter] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("filter") === "loa_pending" ? "loa_pending" : "all";
    });
    const [exportOpen, setExportOpen] = useState(null); // null | "students" | "loa"
    const [wizardPresetEmail, setWizardPresetEmail] = useState("");
    const [deletingId, setDeletingId] = useState(null);
    const [restoringId, setRestoringId] = useState(null);
    const [purgingId, setPurgingId] = useState(null);
    const [purgeTarget, setPurgeTarget] = useState(null);

    const removeStudent = useCallback(
        async (s) => {
            setDeletingId(s.id);
            try {
                await api.delete(`/admin/users/${s.id}`);
                setStudents((prev) => prev.filter((x) => x.id !== s.id));
                // Optimistically push into the trash list so a quick tab-switch shows it.
                setTrashed((prev) => [
                    {
                        id: s.id,
                        name: s.name,
                        email: s.email,
                        role: "student",
                        is_active: false,
                        is_deleted: true,
                        created_at: s.created_at,
                        deleted_at: new Date().toISOString(),
                    },
                    ...prev,
                ]);
                toast.success(
                    `${s.name || s.email} moved to trash. Restore them anytime from the Trash tab.`,
                );
            } catch (err) {
                const msg =
                    err?.response?.data?.detail ||
                    "Could not remove this student. Try again.";
                toast.error(typeof msg === "string" ? msg : "Delete failed.");
            } finally {
                setDeletingId(null);
            }
        },
        [],
    );

    const restoreStudent = useCallback(async (s) => {
        setRestoringId(s.id);
        try {
            await api.post(`/admin/users/${s.id}/restore`);
            setTrashed((prev) => prev.filter((x) => x.id !== s.id));
            toast.success(`${s.name || s.email} restored. Reload the active list to see them.`);
            // Refresh active list so the restored student reappears with full enrolment data
            try {
                const { data } = await api.get("/admin/students");
                setStudents(data || []);
            } catch (e) {
                /* non-fatal */
            }
        } catch (err) {
            const msg =
                err?.response?.data?.detail || "Could not restore. Try again.";
            toast.error(typeof msg === "string" ? msg : "Restore failed.");
        } finally {
            setRestoringId(null);
        }
    }, []);

    const purgeStudent = useCallback(async (s) => {
        setPurgingId(s.id);
        try {
            await api.delete(`/admin/users/${s.id}/purge`);
            setTrashed((prev) => prev.filter((x) => x.id !== s.id));
            toast.success(
                `${s.name || s.email} permanently deleted along with all their data.`,
            );
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "Could not permanently delete this student. Try again.";
            toast.error(typeof msg === "string" ? msg : "Purge failed.");
        } finally {
            setPurgingId(null);
        }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, t, p, ext] = await Promise.all([
                api.get("/admin/students"),
                api.get("/admin/users", { params: { include_deleted: true } }),
                api.get("/programs", { params: { include_archived: true } }),
                api.get("/admin/extension-requests").catch(() => ({ data: [] })),
            ]);
            setStudents(s.data || []);
            setTrashed(
                (t.data || [])
                    .filter((u) => u.role === "student")
                    .sort(
                        (a, b) =>
                            new Date(b.deleted_at || 0) -
                            new Date(a.deleted_at || 0),
                    ),
            );
            setPrograms(
                (p.data || [])
                    .filter((x) => !x.is_deleted)
                    .sort((a, b) => a.title.localeCompare(b.title)),
            );
            setExtensionRequests(ext.data || []);
        } finally {
            setLoading(false);
        }
    }, []);

    const refreshExtensions = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/extension-requests");
            setExtensionRequests(data || []);
        } catch (e) {
            /* non-fatal */
        }
    }, []);

    // Initial load on mount.
    useEffect(() => {
        load();
    }, [load]);

    // (Background polling removed — React 19's strict set-state-in-effect
    // rule disallows the pattern. Admins can refresh the page if they want
    // a live "online now" dot.)


    const filtered = useMemo(() => {
        const source = view === "trash" ? trashed : students;
        const q = filter.trim().toLowerCase();
        let base = source;
        // Pending-LOA pill narrows to students with at least one enrollment
        // whose loa_status is "pending". Lets the registrar see exactly
        // who needs an LOA issued without scanning the whole table.
        if (activeFilter === "loa_pending" && view !== "trash") {
            base = base.filter(
                (s) => (s.enrollments || []).some((e) => e.loa_status === "pending"),
            );
        }
        if (!q) return base;
        return base.filter(
            (s) =>
                s.name?.toLowerCase().includes(q) ||
                s.email?.toLowerCase().includes(q) ||
                s.enrollments?.some((e) =>
                    e.program_title?.toLowerCase().includes(q),
                ),
        );
    }, [students, trashed, view, filter, activeFilter]);

    // Compute the pending-LOA count from current students data — keeps the
    // pill badge accurate without a separate API call or extra state.
    const loaPendingCount = useMemo(
        () => students.reduce(
            (acc, s) => acc + ((s.enrollments || []).filter((e) => e.loa_status === "pending").length),
            0,
        ),
        [students],
    );

    const totals = useMemo(() => {
        const totalStudents = students.length;
        const totalEnrollments = students.reduce(
            (acc, s) => acc + (s.enrollment_count || 0),
            0,
        );
        const offlineEnrollments = students.reduce(
            (acc, s) =>
                acc +
                (s.enrollments || []).filter((e) => e.payment_mode === "paid_offline").length,
            0,
        );
        const completed = students.reduce(
            (acc, s) =>
                acc + (s.enrollments || []).filter((e) => e.completed_at).length,
            0,
        );
        return { totalStudents, totalEnrollments, offlineEnrollments, completed };
    }, [students]);

    return (
        <div
            className="max-w-7xl mx-auto px-6 py-10 pb-24"
            data-testid="admin-students-page"
        >
            <Link to="/admin" className="text-sm text-muted-foreground hover:text-primary">
                ← Back
            </Link>
            <div className="flex items-end justify-between mt-2 mb-8 flex-wrap gap-3 anim-fade-up">
                <div>
                    <div className="inline-flex items-center gap-1.5 chip chip-primary">
                        <GraduationCap className="h-3 w-3" />
                        Learners
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl text-foreground mt-3 tracking-tight">
                        Students
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
                        Every learner enrolled at Cambridge — paid online via Stripe, paid in
                        person at the college, or admin-granted access. Track progress,
                        enrolment type, and tap into a guided wizard to add new students.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={() => setExportOpen("students")}
                        variant="outline"
                        className="gap-1.5"
                        data-testid="open-export-students-btn"
                        size="lg"
                    >
                        <Download className="h-4 w-4" />
                        Export CSV
                    </Button>
                    <Button
                        onClick={() => setExportOpen("loa")}
                        variant="outline"
                        className="gap-1.5"
                        data-testid="open-export-loa-btn"
                        size="lg"
                    >
                        <FileText className="h-4 w-4" />
                        LOA / COL log
                    </Button>
                    <Button
                        onClick={() => setWizardOpen(true)}
                        className="bg-primary hover:bg-primary/90 gap-1.5 shadow-md shadow-primary/10"
                        data-testid="open-enrollment-wizard-btn"
                        size="lg"
                    >
                        <UserPlus className="h-4 w-4" />
                        Enrol new student
                    </Button>
                </div>
            </div>

            {/* KPI Tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 anim-stagger">
                <KpiTile
                    label="Total students"
                    value={totals.totalStudents}
                    Icon={GraduationCap}
                    testId="kpi-total-students"
                />
                <KpiTile
                    label="Active enrolments"
                    value={totals.totalEnrollments}
                    Icon={BookOpen}
                    testId="kpi-total-enrollments"
                />
                <KpiTile
                    label="Walk-in (offline)"
                    value={totals.offlineEnrollments}
                    Icon={Wallet}
                    testId="kpi-offline-enrollments"
                />
                <KpiTile
                    label="Completed programs"
                    value={totals.completed}
                    Icon={CheckCircle2}
                    testId="kpi-completed"
                />
            </div>

            {/* Search + Tabs */}
            <Tabs
                value={view}
                onValueChange={(v) => {
                    setView(v);
                    setFilter("");
                }}
                className="mb-4"
            >
                <TabsList data-testid="students-view-tabs">
                    <TabsTrigger value="active" data-testid="students-tab-active">
                        Active
                        <Badge
                            variant="outline"
                            className="ml-2 text-[10px] border-border bg-background"
                        >
                            {students.length}
                        </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="trash" data-testid="students-tab-trash">
                        Trash
                        <Badge
                            variant="outline"
                            className="ml-2 text-[10px] border-border bg-background"
                        >
                            {trashed.length}
                        </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="requests" data-testid="students-tab-requests">
                        Requests
                        <Badge
                            variant="outline"
                            className={`ml-2 text-[10px] ${
                                extensionRequests.filter((r) => r.status === "pending").length > 0
                                    ? "border-amber-500/40 text-amber-700 bg-amber-100/60"
                                    : "border-border bg-background"
                            }`}
                        >
                            {extensionRequests.filter((r) => r.status === "pending").length}
                        </Badge>
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder={
                            view === "trash"
                                ? "Search trashed students…"
                                : view === "requests"
                                    ? "Filter requests… (use the table below)"
                                    : "Search name, email or program…"
                        }
                        className="pl-9"
                        data-testid="students-filter-input"
                        disabled={view === "requests"}
                    />
                </div>
                {view === "active" && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <Button
                            size="sm"
                            variant={activeFilter === "all" ? "default" : "outline"}
                            onClick={() => setActiveFilter("all")}
                            data-testid="filter-all"
                            className="h-8"
                        >
                            All students
                        </Button>
                        <Button
                            size="sm"
                            variant={activeFilter === "loa_pending" ? "default" : "outline"}
                            onClick={() => setActiveFilter("loa_pending")}
                            data-testid="filter-loa-pending"
                            className={`h-8 gap-1.5 ${activeFilter === "loa_pending" ? "" : "border-amber-500 text-amber-700 hover:bg-amber-50"}`}
                            title="Paid enrollments that still need a Letter of Acceptance"
                        >
                            <Mail className="h-3.5 w-3.5" />
                            Pending LOA
                            {loaPendingCount > 0 && (
                                <span
                                    className="ml-0.5 inline-flex items-center justify-center min-w-[20px] h-[18px] rounded-full bg-amber-500 text-white text-[10px] font-semibold px-1.5"
                                    data-testid="loa-pending-count"
                                >
                                    {loaPendingCount}
                                </span>
                            )}
                        </Button>
                    </div>
                )}
                {view !== "requests" && (
                    <div className="flex items-center gap-3 flex-wrap">
                        <p className="text-xs text-muted-foreground">
                            Showing <strong>{filtered.length}</strong> of{" "}
                            {view === "trash" ? trashed.length : students.length}
                        </p>
                        {view === "trash" && (
                            <EmptyTrashButton
                                resource="students"
                                count={trashed.length}
                                label="trashed students"
                                onEmptied={() => setTrashed([])}
                                testId="students-empty-trash"
                            />
                        )}
                    </div>
                )}
            </div>

            {view === "requests" ? (
                <ExtensionRequestsTab
                    rows={extensionRequests}
                    loading={loading}
                    onRefresh={refreshExtensions}
                />
            ) : view === "trash" ? (
                <TrashTable
                    rows={filtered}
                    loading={loading}
                    onRestore={restoreStudent}
                    onPurgeClick={setPurgeTarget}
                    restoringId={restoringId}
                    purgingId={purgingId}
                    hasFilter={!!filter}
                />
            ) : (
            <Card className="surface-card border-border">
                <CardContent className="p-0">
                    <Table className="data-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Student</TableHead>
                                <TableHead>Programs</TableHead>
                                <TableHead className="w-44">Overall progress</TableHead>
                                <TableHead className="w-32">Status</TableHead>
                                <TableHead className="w-36">Last seen</TableHead>
                                <TableHead className="w-32">Joined</TableHead>
                                <TableHead className="w-16 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-12">
                                        <div className="text-sm text-muted-foreground">
                                            Loading students…
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : filtered.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-12">
                                        <div className="empty-state !py-8">
                                            <div className="empty-icon-wrap">
                                                <Sparkles className="h-8 w-8 text-primary" />
                                            </div>
                                            <p className="font-display text-lg text-foreground">
                                                {filter ? "No matches" : "No students yet"}
                                            </p>
                                            <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                                                {filter
                                                    ? "Try a different search term."
                                                    : "Enrol your first walk-in student to populate this list."}
                                            </p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filtered.map((s) => (
                                    <TableRow
                                        key={s.id}
                                        data-testid={`student-row-${s.id}`}
                                    >
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <Link
                                                    to={`/admin/students/${s.id}`}
                                                    className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-display text-sm ring-1 ring-primary/15 hover:ring-primary/40 transition"
                                                    data-testid={`student-avatar-link-${s.id}`}
                                                    aria-label={`Open ${s.name || s.email}'s profile`}
                                                >
                                                    {s.name?.charAt(0).toUpperCase() || "?"}
                                                </Link>
                                                <div className="min-w-0">
                                                    <div className="font-medium text-foreground truncate">
                                                        <Link
                                                            to={`/admin/students/${s.id}`}
                                                            className="hover:text-primary hover:underline underline-offset-2 transition"
                                                            data-testid={`student-name-link-${s.id}`}
                                                        >
                                                            {s.name}
                                                        </Link>
                                                        {s.created_by_admin && (
                                                            <Badge
                                                                variant="outline"
                                                                className="ml-2 text-[10px] border-accent/40 text-accent-foreground bg-accent/10"
                                                                title="Created by an admin"
                                                            >
                                                                Admin-added
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                                                        <Mail className="h-3 w-3" />
                                                        {s.email}
                                                    </div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {s.enrollments?.length === 0 ? (
                                                <span className="text-xs text-muted-foreground italic">
                                                    Not enrolled
                                                </span>
                                            ) : (
                                                <div className="space-y-1.5">
                                                    {s.enrollments.slice(0, 3).map((e) => (
                                                        <TooltipProvider key={e.enrollment_id}>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <span className="text-xs text-foreground truncate max-w-[200px]">
                                                                            {e.program_title}
                                                                        </span>
                                                                        <PaymentBadge
                                                                            mode={e.payment_mode}
                                                                        />
                                                                        <span className="text-[10px] text-muted-foreground">
                                                                            {e.progress_percent}%
                                                                        </span>
                                                                    </div>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    {e.chapters_completed}/
                                                                    {e.total_chapters} chapters ·
                                                                    enrolled{" "}
                                                                    {formatDate(e.enrolled_at)}
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    ))}
                                                    {s.enrollments.length > 3 && (
                                                        <p className="text-[10px] text-muted-foreground">
                                                            + {s.enrollments.length - 3} more
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Progress
                                                    value={s.overall_progress_percent || 0}
                                                    className="h-2 flex-1"
                                                />
                                                <span className="text-xs text-muted-foreground tabular-nums w-9 text-right">
                                                    {s.overall_progress_percent || 0}%
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {s.is_active ? (
                                                <Badge
                                                    variant="outline"
                                                    className="border-primary/40 text-primary bg-primary/5 text-[10px]"
                                                >
                                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                                    Active
                                                </Badge>
                                            ) : (
                                                <Badge
                                                    variant="outline"
                                                    className="text-[10px]"
                                                >
                                                    Disabled
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell data-testid={`student-presence-${s.id}`}>
                                            <PresenceIndicator lastSeenAt={s.last_seen_at} />
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {formatDate(s.created_at)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <StudentRowActions
                                                    student={s}
                                                    onEnrolAgain={(stu) => {
                                                        setWizardPresetEmail(stu.email);
                                                        setWizardOpen(true);
                                                    }}
                                                    onEdit={(stu) => {
                                                        setEditingStudent(stu);
                                                        setEditOpen(true);
                                                    }}
                                                />
                                                <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                                        title="Remove student"
                                                        data-testid={`student-delete-btn-${s.id}`}
                                                        disabled={deletingId === s.id}
                                                    >
                                                        {deletingId === s.id ? (
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            <Trash2 className="h-4 w-4" />
                                                        )}
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent
                                                    data-testid={`student-delete-dialog-${s.id}`}
                                                >
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>
                                                            Remove {s.name || s.email}?
                                                        </AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            This soft-deletes the student. Their
                                                            login is revoked and they&apos;re hidden
                                                            from the active list. Their
                                                            enrollments, progress, and certificates
                                                            stay archived. You can restore them
                                                            from the Trash later.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel
                                                            data-testid={`student-delete-cancel-${s.id}`}
                                                        >
                                                            Cancel
                                                        </AlertDialogCancel>
                                                        <AlertDialogAction
                                                            onClick={() => removeStudent(s)}
                                                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                            data-testid={`student-delete-confirm-${s.id}`}
                                                        >
                                                            Remove student
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
            )}

            <StudentEnrollmentWizard
                open={wizardOpen}
                onOpenChange={(open) => {
                    setWizardOpen(open);
                    if (!open) setWizardPresetEmail("");
                }}
                programs={programs}
                onDone={load}
                presetEmail={wizardPresetEmail}
            />

            <EditStudentDialog
                open={editOpen}
                onOpenChange={(v) => {
                    setEditOpen(v);
                    if (!v) setEditingStudent(null);
                }}
                student={editingStudent}
                onSaved={load}
            />

            <ExportCsvDialog
                open={exportOpen !== null}
                onOpenChange={(open) => setExportOpen(open ? exportOpen : null)}
                report={exportOpen || "students"}
                programs={programs}
            />

            <RequestPurgeDialog
                open={!!purgeTarget}
                onOpenChange={(o) => !o && setPurgeTarget(null)}
                resourceType="user"
                resourceId={purgeTarget?.id}
                resourceLabel={purgeTarget ? (purgeTarget.name || purgeTarget.email) : ""}
                onConfirmPurge={async () => {
                    if (purgeTarget) await purgeStudent(purgeTarget);
                    setPurgeTarget(null);
                }}
                confirmTestId={`student-purge-confirm-${purgeTarget?.id || ""}`}
                cancelTestId={`student-purge-cancel-${purgeTarget?.id || ""}`}
            />
        </div>
    );
}

function TrashTable({
    rows,
    loading,
    onRestore,
    onPurgeClick,
    restoringId,
    purgingId,
    hasFilter,
}) {
    return (
        <Card className="surface-card border-border" data-testid="students-trash-card">
            <CardContent className="p-0">
                <Table className="data-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead>Student</TableHead>
                            <TableHead className="w-44">Trashed</TableHead>
                            <TableHead className="w-40">Joined</TableHead>
                            <TableHead className="w-44 text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={4} className="text-center py-12">
                                    <div className="text-sm text-muted-foreground">
                                        Loading trash…
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} className="text-center py-12">
                                    <div className="empty-state !py-8">
                                        <div className="empty-icon-wrap">
                                            <Trash2 className="h-8 w-8 text-primary" />
                                        </div>
                                        <p className="font-display text-lg text-foreground">
                                            {hasFilter ? "No matches" : "Trash is empty"}
                                        </p>
                                        <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                                            {hasFilter
                                                ? "Try a different search term."
                                                : "Students you remove from the active list will appear here. You can restore them anytime."}
                                        </p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((s) => (
                                <TableRow
                                    key={s.id}
                                    data-testid={`trash-row-${s.id}`}
                                    className="opacity-90"
                                >
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <div className="h-9 w-9 rounded-full bg-muted text-muted-foreground flex items-center justify-center font-display text-sm ring-1 ring-border">
                                                {s.name?.charAt(0).toUpperCase() || "?"}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-medium text-foreground truncate">
                                                    {s.name || "(no name)"}
                                                </div>
                                                <div className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                                                    <Mail className="h-3 w-3" />
                                                    {s.email}
                                                </div>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground">
                                        {formatDate(s.deleted_at)}
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground">
                                        {formatDate(s.created_at)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="inline-flex items-center gap-1.5">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="border-primary/40 text-primary hover:bg-primary/10 gap-1.5"
                                                onClick={() => onRestore(s)}
                                                disabled={
                                                    restoringId === s.id || purgingId === s.id
                                                }
                                                data-testid={`student-restore-btn-${s.id}`}
                                                title="Restore student"
                                            >
                                                {restoringId === s.id ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <RotateCcw className="h-3.5 w-3.5" />
                                                )}
                                                Restore
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                                title="Delete forever"
                                                onClick={() => onPurgeClick(s)}
                                                data-testid={`student-purge-btn-${s.id}`}
                                                disabled={
                                                    restoringId === s.id ||
                                                    purgingId === s.id
                                                }
                                            >
                                                {purgingId === s.id ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <Trash2 className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}
